import React, { useEffect, useState } from "react";
import {
  Trash2,
  Plus,
  LogOut,
  Facebook,
  Calendar,
  Image as ImageIcon,
  Newspaper,
  Building2,
  Images as ImagesIcon,
  Quote,
  Star as StarIcon,
  Sun,
  MapPin,
  Battery,
  CheckCircle2,
  Globe,
  AlertCircle,
  Pencil,
  X as XIcon,
  Inbox,
  Mail,
  Phone,
  User as UserIcon,
  Calculator,
  LayoutDashboard,
  Briefcase,
  ExternalLink,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { findByShortId, pageParam, shortId } from "../components/quotes/format";
import AdminDashboard from "../components/AdminDashboard";
import QuoteAdmin from "../components/quotes/QuoteAdmin";
import Seo from "../components/Seo";
import ImageUploader, { resolveImageUrl } from "../components/ImageUploader";
import MediaLibrary from "../components/MediaLibrary";
import RichTextEditor from "../components/RichTextEditor";
import SocialPublishPanel from "../components/SocialPublishPanel";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_STORAGE_KEY = "energeide_admin_token";
const PROJECT_TYPES = ["Residenziale", "Commerciale", "Industriale"];
const MONTHS_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

const parseItalianMonthYear = (value) => {
  if (!value) return { month: "", year: String(new Date().getFullYear()) };
  const text = String(value).trim();
  const yearMatch = text.match(/(20\d{2}|19\d{2})/);
  const year = yearMatch ? yearMatch[1] : String(new Date().getFullYear());
  const lower = text.toLowerCase();
  const month = MONTHS_IT.find((m) => lower.includes(m.toLowerCase())) || "";
  return { month, year };
};

const formatItalianMonthYear = (month, year) => {
  if (month && year) return `${month} ${year}`;
  if (year) return year;
  return "";
};

const useAdminToken = () => {
  const [token, setToken] = useState(() =>
    typeof window !== "undefined" ? localStorage.getItem(TOKEN_STORAGE_KEY) : null
  );

  const save = (value) => {
    if (value) {
      localStorage.setItem(TOKEN_STORAGE_KEY, value);
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
    setToken(value);
  };

  return [token, save];
};

const LoginForm = ({ onLogin }) => {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: value }),
      });
      if (!res.ok) {
        setError("Token non valido. Riprova.");
        return;
      }
      onLogin(value);
    } catch (err) {
      setError("Errore di rete. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12 bg-gray-50">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-sm bg-white rounded-2xl shadow-lg p-8 border border-gray-100"
      >
        <h1 className="text-2xl font-montserrat font-bold text-[#0A1F44] mb-1">
          Area Riservata
        </h1>
        <p className="text-sm text-gray-500 mb-6">
          Inserisci il token amministratore per gestire il sito.
        </p>
        <label className="block text-xs font-semibold text-[#0A1F44] mb-2 uppercase tracking-wider">
          Admin Token
        </label>
        <input
          data-testid="admin-token-input"
          type="password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
          className="w-full px-4 h-11 border border-gray-200 rounded-md focus:outline-none focus:border-[#0FB36B] focus:ring-2 focus:ring-[#0FB36B]/10 transition"
          placeholder="••••••••••••"
          required
        />
        {error ? (
          <div
            data-testid="admin-login-error"
            className="mt-3 flex items-center gap-2 text-sm text-red-600"
          >
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        ) : null}
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading || !value}
          className="mt-5 w-full h-11 bg-[#0A1F44] hover:bg-[#0d2855] disabled:opacity-50 text-white rounded-md font-montserrat font-semibold text-sm transition-colors"
        >
          {loading ? "Verifica…" : "Accedi"}
        </button>
      </form>
    </div>
  );
};

const FieldLabel = ({ children, icon: Icon }) => (
  <label className="text-xs font-semibold text-[#0A1F44] mb-1.5 uppercase tracking-wider flex items-center gap-1.5">
    {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
    {children}
  </label>
);

const TextInput = ({ testid, value, onChange, placeholder, type = "text" }) => (
  <input
    data-testid={testid}
    type={type}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    className="w-full px-4 h-11 border border-gray-200 rounded-md focus:outline-none focus:border-[#0FB36B] focus:ring-2 focus:ring-[#0FB36B]/10 transition"
  />
);

const Feedback = ({ feedback }) =>
  feedback ? (
    <div
      data-testid="form-feedback"
      className={`flex items-center gap-2 text-sm ${
        feedback.type === "success" ? "text-[#0FB36B]" : "text-red-600"
      }`}
    >
      {feedback.type === "success" ? (
        <CheckCircle2 className="w-4 h-4" />
      ) : (
        <AlertCircle className="w-4 h-4" />
      )}
      {feedback.text}
    </div>
  ) : null;

// -------------------------------------------------------------------
// BLOG POST FORM (create + edit)
// -------------------------------------------------------------------
const PostForm = ({ token, onSaved, editing, onCancelEdit }) => {
  const isEdit = Boolean(editing);
  const emptyForm = {
    title: "",
    content: "",
    images: [],
    facebook_url: "",
    published_at: "",
    social: { facebook: true, instagram: true },
  };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => {
    if (editing) {
      const pa = editing.published_at
        ? new Date(editing.published_at).toISOString().slice(0, 10)
        : "";
      setForm({
        title: editing.title || "",
        content: editing.content || "",
        images: Array.isArray(editing.images)
          ? [...editing.images]
          : editing.image_url
          ? [editing.image_url]
          : [],
        facebook_url: editing.facebook_url || "",
        published_at: pa,
      });
    } else {
      setForm(emptyForm);
    }
    setFeedback(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const setImages = (images) => setForm((s) => ({ ...s, images }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      const url = isEdit
        ? `${API_URL}/api/posts/${editing.id}`
        : `${API_URL}/api/posts`;
      const method = isEdit ? "PUT" : "POST";
      const body = {
        title: form.title,
        content: form.content,
        images: form.images,
        facebook_url: form.facebook_url,
      };
      // Pubblicazione social: solo in creazione (non si "ri-pubblica" un edit)
      if (!isEdit) {
        if (form.social.facebook)  body.publish_to_facebook  = true;
        if (form.social.instagram) body.publish_to_instagram = true;
      }
      // published_at: solo in edit (per cambiare la data del post)
      if (isEdit && form.published_at) {
        body.published_at = new Date(form.published_at + "T12:00:00").toISOString();
      }
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Token": token,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        setFeedback({ type: "error", text: "Errore nel salvataggio." });
        return;
      }
      const data = await res.json();
      setLastSaved(data);
      const parts = [isEdit ? "Post aggiornato!" : "Post pubblicato!"];
      if (data?.social?.facebook_ok)  parts.push("FB ✓");
      if (data?.social?.instagram_ok) parts.push("IG ✓");
      if (data?.social?.facebook_error)  parts.push("FB ✗ " + data.social.facebook_error);
      if (data?.social?.instagram_error) parts.push("IG ✗ " + data.social.instagram_error);
      setFeedback({ type: "success", text: parts.join(" · ") });
      if (!isEdit) setForm(emptyForm);
      onSaved?.(data, isEdit);
    } catch (err) {
      setFeedback({ type: "error", text: "Errore di rete." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      data-testid="admin-post-form"
      onSubmit={submit}
      className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 space-y-4"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-montserrat font-bold text-[#0A1F44] flex items-center gap-2">
          {isEdit ? (
            <Pencil className="w-5 h-5 text-[#F4C542]" />
          ) : (
            <Plus className="w-5 h-5 text-[#0FB36B]" />
          )}
          {isEdit ? "Modifica Post" : "Nuovo Post"}
        </h2>
        {isEdit ? (
          <button
            type="button"
            onClick={onCancelEdit}
            className="text-xs text-gray-500 hover:text-[#0A1F44] inline-flex items-center gap-1"
          >
            <XIcon className="w-3.5 h-3.5" />
            Annulla
          </button>
        ) : null}
      </div>

      <div>
        <FieldLabel>Titolo *</FieldLabel>
        <TextInput
          testid="post-title-input"
          value={form.title}
          onChange={update("title")}
          placeholder="Titolo del post"
        />
      </div>

      <div>
        <FieldLabel>Contenuto *</FieldLabel>
        <RichTextEditor
          testid="post-content-input"
          value={form.content}
          onChange={(html) => setForm((f) => ({ ...f, content: html }))}
          placeholder="Scrivi il contenuto del post..."
          minHeight={200}
        />
      </div>

      <ImageUploader
        token={token}
        images={form.images}
        onChange={setImages}
        label="Immagini del post"
        testIdPrefix="post"
      />

      {isEdit && (
        <div>
          <FieldLabel icon={Calendar}>Data di pubblicazione</FieldLabel>
          <input
            data-testid="post-date-input"
            type="date"
            value={form.published_at}
            onChange={update("published_at")}
            className="w-full px-4 h-11 border border-gray-200 rounded-md focus:outline-none focus:border-[#0FB36B] focus:ring-2 focus:ring-[#0FB36B]/10 transition"
          />
          <p className="text-[11px] text-gray-400 mt-1">
            Cambiando la data, il post si riordina nella lista pubblica.
          </p>
        </div>
      )}

      <SocialPublishPanel
        value={form.social}
        onChange={(next) => setForm((s) => ({ ...s, social: next }))}
        lastSaved={
          lastSaved || (isEdit && (editing?.facebook_url || editing?.instagram_url)
            ? {
                id: editing.id,
                title: editing.title,
                facebook_url: editing.facebook_url,
                instagram_url: editing.instagram_url,
              }
            : null)
        }
        isEdit={isEdit}
      />

      <Feedback feedback={feedback} />

      <button
        data-testid="post-submit-button"
        type="submit"
        disabled={saving || !form.title || !form.content}
        className="inline-flex items-center justify-center gap-2 h-11 px-6 bg-[#F4C542] hover:bg-[#e6b838] disabled:opacity-50 text-[#0A1F44] rounded-md font-montserrat font-bold text-sm shadow-md transition-colors"
      >
        {saving ? "Salvataggio…" : isEdit ? "Salva Modifiche" : "Pubblica Post"}
      </button>
    </form>
  );
};

// -------------------------------------------------------------------
// PROJECT FORM (create + edit)
// -------------------------------------------------------------------
const ProjectForm = ({ token, onSaved, editing, onCancelEdit }) => {
  const isEdit = Boolean(editing);
  const currentYear = String(new Date().getFullYear());
  const buildEmpty = () => ({
    title: "",
    location: "",
    region: "",
    type: "Residenziale",
    power: "",
    storage: "",
    month: MONTHS_IT[new Date().getMonth()],
    year: currentYear,
    description: "",
    images: [],
    social: { facebook: true, instagram: true },
  });
  const [form, setForm] = useState(buildEmpty());
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => {
    if (editing) {
      const { month, year } = parseItalianMonthYear(editing.year);
      setForm({
        title: editing.title || "",
        location: editing.location || "",
        region: editing.region || "",
        type: editing.type || "Residenziale",
        power: editing.power || "",
        storage: editing.storage || "",
        month,
        year,
        description: editing.description || "",
        images: Array.isArray(editing.images)
          ? [...editing.images]
          : editing.image
          ? [editing.image]
          : [],
      });
    } else {
      setForm(buildEmpty());
    }
    setFeedback(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const setImages = (images) => setForm((s) => ({ ...s, images }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      const body = {
        title: form.title,
        location: form.location,
        region: form.region,
        type: form.type,
        power: form.power,
        storage: form.storage,
        year: formatItalianMonthYear(form.month, form.year),
        description: form.description,
        images: form.images,
      };
      // Social publish solo in creazione
      if (!isEdit) {
        if (form.social.facebook)  body.publish_to_facebook  = true;
        if (form.social.instagram) body.publish_to_instagram = true;
      }
      const url = isEdit
        ? `${API_URL}/api/admin/projects/${editing.id}`
        : `${API_URL}/api/admin/projects`;
      const method = isEdit ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Token": token,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        setFeedback({ type: "error", text: "Errore nel salvataggio." });
        return;
      }
      const data = await res.json().catch(() => ({}));
      setLastSaved(data);
      const parts = [isEdit ? "Progetto aggiornato!" : "Progetto pubblicato!"];
      if (data?.social?.facebook_ok)  parts.push("FB ✓");
      if (data?.social?.instagram_ok) parts.push("IG ✓");
      if (data?.social?.facebook_error)  parts.push("FB ✗ " + data.social.facebook_error);
      if (data?.social?.instagram_error) parts.push("IG ✗ " + data.social.instagram_error);
      setFeedback({ type: "success", text: parts.join(" · ") });
      if (!isEdit) setForm(buildEmpty());
      onSaved?.(isEdit);
    } catch (err) {
      setFeedback({ type: "error", text: "Errore di rete." });
    } finally {
      setSaving(false);
    }
  };

  // Genera lista anni dal corrente fino a 10 anni indietro
  const yearOptions = Array.from({ length: 12 }, (_, i) =>
    String(parseInt(currentYear, 10) - i + 2)
  );

  return (
    <form
      data-testid="admin-project-form"
      onSubmit={submit}
      className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 space-y-4"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-montserrat font-bold text-[#0A1F44] flex items-center gap-2">
          {isEdit ? (
            <Pencil className="w-5 h-5 text-[#F4C542]" />
          ) : (
            <Plus className="w-5 h-5 text-[#0FB36B]" />
          )}
          {isEdit ? "Modifica Progetto" : "Nuovo Progetto"}
        </h2>
        {isEdit ? (
          <button
            type="button"
            onClick={onCancelEdit}
            className="text-xs text-gray-500 hover:text-[#0A1F44] inline-flex items-center gap-1"
          >
            <XIcon className="w-3.5 h-3.5" />
            Annulla
          </button>
        ) : null}
      </div>

      <div>
        <FieldLabel>Titolo *</FieldLabel>
        <TextInput
          testid="project-title-input"
          value={form.title}
          onChange={update("title")}
          placeholder="Es. Impianto fotovoltaico 6 kW residenziale"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <FieldLabel icon={MapPin}>Località</FieldLabel>
          <TextInput
            testid="project-location-input"
            value={form.location}
            onChange={update("location")}
            placeholder="Es. Quadrelle (AV)"
          />
        </div>
        <div>
          <FieldLabel>Regione</FieldLabel>
          <TextInput
            testid="project-region-input"
            value={form.region}
            onChange={update("region")}
            placeholder="Es. Campania"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <FieldLabel>Tipo</FieldLabel>
          <select
            data-testid="project-type-input"
            value={form.type}
            onChange={update("type")}
            className="w-full px-4 h-11 border border-gray-200 rounded-md focus:outline-none focus:border-[#0FB36B] focus:ring-2 focus:ring-[#0FB36B]/10 transition bg-white"
          >
            {PROJECT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div>
          <FieldLabel icon={Sun}>Potenza</FieldLabel>
          <TextInput
            testid="project-power-input"
            value={form.power}
            onChange={update("power")}
            placeholder="Es. 6 kW"
          />
        </div>
        <div>
          <FieldLabel icon={Battery}>Accumulo</FieldLabel>
          <TextInput
            testid="project-storage-input"
            value={form.storage}
            onChange={update("storage")}
            placeholder="Es. 10 kWh"
          />
        </div>
      </div>

      <div>
        <FieldLabel icon={Calendar}>Data installazione</FieldLabel>
        <div className="grid grid-cols-2 gap-2">
          <select
            data-testid="project-month-input"
            value={form.month}
            onChange={update("month")}
            className="w-full px-4 h-11 border border-gray-200 rounded-md focus:outline-none focus:border-[#0FB36B] focus:ring-2 focus:ring-[#0FB36B]/10 transition bg-white"
          >
            <option value="">— Mese —</option>
            {MONTHS_IT.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <select
            data-testid="project-year-input"
            value={form.year}
            onChange={update("year")}
            className="w-full px-4 h-11 border border-gray-200 rounded-md focus:outline-none focus:border-[#0FB36B] focus:ring-2 focus:ring-[#0FB36B]/10 transition bg-white"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <p className="text-[11px] text-gray-400 mt-1">
          Determina l&apos;ordine dei progetti sulla pagina (più recenti prima).
        </p>
      </div>

      <div>
        <FieldLabel>Descrizione</FieldLabel>
        <RichTextEditor
          testid="project-description-input"
          value={form.description}
          onChange={(html) => setForm((f) => ({ ...f, description: html }))}
          placeholder="Descrizione del progetto..."
          minHeight={160}
        />
      </div>

      <ImageUploader
        token={token}
        images={form.images}
        onChange={setImages}
        label="Foto del progetto"
        testIdPrefix="project"
      />

      <SocialPublishPanel
        value={form.social}
        onChange={(next) => setForm((s) => ({ ...s, social: next }))}
        lastSaved={
          lastSaved || (isEdit && (editing?.facebook_url || editing?.instagram_url)
            ? {
                id: editing.id,
                title: editing.title,
                facebook_url: editing.facebook_url,
                instagram_url: editing.instagram_url,
              }
            : null)
        }
        isEdit={isEdit}
      />

      <Feedback feedback={feedback} />

      <button
        data-testid="project-submit-button"
        type="submit"
        disabled={saving || !form.title}
        className="inline-flex items-center justify-center gap-2 h-11 px-6 bg-[#F4C542] hover:bg-[#e6b838] disabled:opacity-50 text-[#0A1F44] rounded-md font-montserrat font-bold text-sm shadow-md transition-colors"
      >
        {saving ? "Salvataggio…" : isEdit ? "Salva Modifiche" : "Pubblica Progetto"}
      </button>
    </form>
  );
};

// -------------------------------------------------------------------
// LISTE
// -------------------------------------------------------------------
const ItemImage = ({ images, fallbackUrl }) => {
  const cover = (Array.isArray(images) && images[0]) || fallbackUrl || "";
  const count = Array.isArray(images) ? images.length : fallbackUrl ? 1 : 0;
  return cover ? (
    <div className="relative shrink-0">
      <img
        src={resolveImageUrl(cover)}
        alt=""
        className="w-16 h-16 rounded-lg object-cover"
      />
      {count > 1 ? (
        <span className="absolute -bottom-1 -right-1 bg-[#0A1F44] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center border-2 border-white">
          {count}
        </span>
      ) : null}
    </div>
  ) : (
    <div className="w-16 h-16 rounded-lg bg-gray-100 shrink-0 flex items-center justify-center">
      <ImageIcon className="w-5 h-5 text-gray-300" />
    </div>
  );
};

const PostList = ({ posts, token, onDelete, onEdit, editingId }) => {
  const [deletingId, setDeletingId] = useState(null);

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo post?")) return;
    setDeletingId(id);
    try {
      const res = await fetch(`${API_URL}/api/posts/${id}`, {
        method: "DELETE",
        headers: { "X-Admin-Token": token },
      });
      if (res.ok) onDelete?.(id);
    } finally {
      setDeletingId(null);
    }
  };

  if (posts.length === 0) {
    return (
      <p
        data-testid="admin-no-posts"
        className="text-sm text-gray-500 italic py-6 text-center bg-gray-50 rounded-xl"
      >
        Ancora nessun post pubblicato.
      </p>
    );
  }

  return (
    <ul data-testid="admin-posts-list" className="space-y-3">
      {posts.map((p) => (
        <li
          key={p.id}
          className={`bg-white border rounded-xl p-4 flex items-start gap-4 transition-colors ${
            editingId === p.id ? "border-[#F4C542] ring-2 ring-[#F4C542]/20" : "border-gray-100"
          }`}
        >
          <ItemImage images={p.images} fallbackUrl={p.image_url} />
          <div className="flex-1 min-w-0">
            <p className="font-montserrat font-semibold text-[#0A1F44] text-sm truncate">
              {p.title}
            </p>
            <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
              <Calendar className="w-3 h-3" />
              {new Date(p.published_at).toLocaleDateString("it-IT")}
            </p>
            <p className="text-sm text-gray-600 mt-1 line-clamp-2">{p.content}</p>
          </div>
          <div className="flex flex-col gap-1">
            <button
              data-testid={`edit-post-${p.id}`}
              type="button"
              onClick={() => onEdit?.(p)}
              className="text-[#0A1F44] hover:text-[#F4C542] p-2 transition-colors"
              aria-label="Modifica post"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              data-testid={`delete-post-${p.id}`}
              type="button"
              onClick={() => remove(p.id)}
              disabled={deletingId === p.id}
              className="text-red-500 hover:text-red-700 disabled:opacity-50 p-2 transition-colors"
              aria-label="Elimina post"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
};

const ProjectList = ({ projects, token, onDelete, onEdit, editingId }) => {
  const [deletingId, setDeletingId] = useState(null);

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo progetto?")) return;
    setDeletingId(id);
    try {
      const res = await fetch(`${API_URL}/api/admin/projects/${id}`, {
        method: "DELETE",
        headers: { "X-Admin-Token": token },
      });
      if (res.ok) onDelete?.(id);
    } finally {
      setDeletingId(null);
    }
  };

  if (projects.length === 0) {
    return (
      <p
        data-testid="admin-no-projects"
        className="text-sm text-gray-500 italic py-6 text-center bg-gray-50 rounded-xl"
      >
        Nessun progetto pubblicato dal pannello. <br />
        <span className="text-[11px] text-gray-400">
          (I progetti dal Google Sheet restano comunque visibili sul sito)
        </span>
      </p>
    );
  }

  return (
    <ul data-testid="admin-projects-list" className="space-y-3">
      {projects.map((p) => (
        <li
          key={p.id}
          className={`bg-white border rounded-xl p-4 flex items-start gap-4 transition-colors ${
            editingId === p.id ? "border-[#F4C542] ring-2 ring-[#F4C542]/20" : "border-gray-100"
          }`}
        >
          <ItemImage images={p.images} fallbackUrl={p.image} />
          <div className="flex-1 min-w-0">
            <p className="font-montserrat font-semibold text-[#0A1F44] text-sm">
              {p.title}
            </p>
            <p className="text-xs text-gray-500 mt-0.5 truncate">
              {p.location} {p.power ? `· ${p.power}` : ""}{" "}
              {p.year ? `· ${p.year}` : ""}
            </p>
            {p.description ? (
              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                {p.description}
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-1">
            <button
              data-testid={`edit-project-${p.id}`}
              type="button"
              onClick={() => onEdit?.(p)}
              className="text-[#0A1F44] hover:text-[#F4C542] p-2 transition-colors"
              aria-label="Modifica progetto"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              data-testid={`delete-project-${p.id}`}
              type="button"
              onClick={() => remove(p.id)}
              disabled={deletingId === p.id}
              className="text-red-500 hover:text-red-700 disabled:opacity-50 p-2 transition-colors"
              aria-label="Elimina progetto"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
};

// -------------------------------------------------------------------
// TESTIMONIAL FORM
// -------------------------------------------------------------------
const emptyTestimonial = {
  name: "",
  location: "",
  role: "",
  text: "",
  date: "",
  rating: 5,
  sort_order: 0,
};

const TestimonialForm = ({ token, editing, onCancelEdit, onSaved }) => {
  const [form, setForm] = useState(emptyTestimonial);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const isEdit = !!editing;

  useEffect(() => {
    setForm(
      editing
        ? {
            name: editing.name || "",
            location: editing.location || "",
            role: editing.role || "",
            text: editing.text || "",
            date: editing.date || "",
            rating: editing.rating || 5,
            sort_order: editing.sort_order || 0,
          }
        : emptyTestimonial
    );
    setFeedback(null);
  }, [editing]);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) return;
    setSaving(true);
    setFeedback(null);
    try {
      const url = isEdit
        ? `${API_URL}/api/admin/testimonials/${editing.id}`
        : `${API_URL}/api/admin/testimonials`;
      const method = isEdit ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Token": token,
        },
        body: JSON.stringify({
          ...form,
          rating: Number(form.rating),
          sort_order: Number(form.sort_order) || 0,
        }),
      });
      if (!res.ok) {
        setFeedback({ type: "error", text: "Errore nel salvataggio." });
        return;
      }
      setFeedback({
        type: "success",
        text: isEdit
          ? "Testimonianza aggiornata!"
          : "Testimonianza pubblicata!",
      });
      if (!isEdit) setForm(emptyTestimonial);
      onSaved?.(isEdit);
    } catch {
      setFeedback({ type: "error", text: "Errore di rete." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-7 shadow-sm space-y-4"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-montserrat font-bold text-[#0A1F44] flex items-center gap-2">
          <Plus className="w-5 h-5 text-[#0FB36B]" />
          {isEdit ? "Modifica Testimonianza" : "Nuova Testimonianza"}
        </h3>
        {isEdit && (
          <button
            type="button"
            onClick={onCancelEdit}
            className="text-xs text-gray-500 hover:text-[#0A1F44] inline-flex items-center gap-1"
          >
            <XIcon className="w-3.5 h-3.5" />
            Annulla
          </button>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <FieldLabel>Nome *</FieldLabel>
          <TextInput
            value={form.name}
            onChange={update("name")}
            placeholder="es. Marco Rossi"
            required
          />
        </div>
        <div>
          <FieldLabel icon={MapPin}>Localita'</FieldLabel>
          <TextInput
            value={form.location}
            onChange={update("location")}
            placeholder="es. Napoli, Campania"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <FieldLabel>Titolo</FieldLabel>
          <TextInput
            value={form.role}
            onChange={update("role")}
            placeholder="es. Ottima esperienza dall'inizio alla fine"
          />
        </div>
        <div>
          <FieldLabel icon={Calendar}>Data installazione</FieldLabel>
          <TextInput
            value={form.date}
            onChange={update("date")}
            placeholder="es. Marzo 2026"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <FieldLabel icon={StarIcon}>Rating (1-5)</FieldLabel>
          <select
            value={form.rating}
            onChange={update("rating")}
            className="w-full px-4 h-11 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#0FB36B] focus:ring-2 focus:ring-[#0FB36B]/10 transition"
          >
            {[5, 4, 3, 2, 1].map((n) => (
              <option key={n} value={n}>
                {n} {n === 1 ? "stella" : "stelle"}
              </option>
            ))}
          </select>
        </div>
        <div>
          <FieldLabel>Ordine (piu' basso = prima)</FieldLabel>
          <TextInput
            type="number"
            value={form.sort_order}
            onChange={update("sort_order")}
            placeholder="0"
          />
        </div>
      </div>

      <div>
        <FieldLabel>Testimonianza *</FieldLabel>
        <textarea
          required
          value={form.text}
          onChange={update("text")}
          rows={5}
          placeholder="Scrivi qui la testimonianza del cliente..."
          className="w-full px-4 py-3 border border-gray-200 rounded-md focus:outline-none focus:border-[#0FB36B] focus:ring-2 focus:ring-[#0FB36B]/10 transition resize-y"
        />
      </div>

      {feedback && (
        <div
          className={`flex items-start gap-2 rounded-md p-3 text-sm ${
            feedback.type === "success"
              ? "bg-[#0FB36B]/10 text-[#0FB36B]"
              : "bg-red-50 text-red-700"
          }`}
        >
          {feedback.type === "success" ? (
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          )}
          {feedback.text}
        </div>
      )}

      <button
        type="submit"
        disabled={saving || !form.name.trim() || !form.text.trim()}
        className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-lg bg-[#F4C542] hover:bg-[#e6b838] text-[#0A1F44] font-montserrat font-bold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isEdit ? "Aggiorna" : "Pubblica Testimonianza"}
      </button>
    </form>
  );
};

// -------------------------------------------------------------------
// TESTIMONIAL LIST (sidebar)
// -------------------------------------------------------------------
const TestimonialList = ({ items, token, editingId, onEdit, onDelete }) => {
  if (items.length === 0) {
    return (
      <div className="bg-gray-50 rounded-xl border border-dashed border-gray-200 p-6 text-center">
        <p className="text-sm text-gray-500">
          Nessuna testimonianza pubblicata.
        </p>
      </div>
    );
  }

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa testimonianza?")) return;
    const res = await fetch(`${API_URL}/api/admin/testimonials/${id}`, {
      method: "DELETE",
      headers: { "X-Admin-Token": token },
    });
    if (res.ok) onDelete?.(id);
  };

  return (
    <ul className="space-y-3">
      {items.map((t) => (
        <li
          key={t.id}
          className={`bg-white p-4 rounded-lg border transition-colors ${
            editingId === t.id
              ? "border-[#F4C542] shadow-md"
              : "border-gray-100 hover:border-gray-200"
          }`}
        >
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-montserrat font-bold text-[#0A1F44] truncate">
                {t.name}
              </p>
              <div className="flex items-center gap-0.5 mt-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <StarIcon
                    key={i}
                    className={`w-3 h-3 ${
                      i < (t.rating || 5)
                        ? "fill-[#F4C542] text-[#F4C542]"
                        : "text-gray-200"
                    }`}
                  />
                ))}
              </div>
            </div>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => onEdit?.(t)}
                aria-label="Modifica"
                className="w-8 h-8 rounded-md hover:bg-gray-100 text-gray-500 hover:text-[#0A1F44] flex items-center justify-center transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => remove(t.id)}
                aria-label="Elimina"
                className="w-8 h-8 rounded-md hover:bg-red-50 text-gray-500 hover:text-red-600 flex items-center justify-center transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-600 line-clamp-2 italic mb-1">
            "{t.text}"
          </p>
          <div className="flex items-center gap-2 text-[11px] text-gray-400">
            {t.location && <span>{t.location}</span>}
            {t.date && (
              <>
                <span>·</span>
                <span>{t.date}</span>
              </>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
};

// -------------------------------------------------------------------
// LEADS / RICHIESTE
// -------------------------------------------------------------------
const LeadsList = ({ leads, token, loading, onDelete }) => {
  const [deletingId, setDeletingId] = useState(null);
  const [openId, setOpenId] = useState(null);

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa richiesta?")) return;
    setDeletingId(id);
    try {
      const res = await fetch(`${API_URL}/api/admin/leads/${id}`, {
        method: "DELETE",
        headers: { "X-Admin-Token": token },
      });
      if (res.ok) onDelete?.(id);
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-400">Caricamento…</p>;
  }

  if (leads.length === 0) {
    return (
      <div className="bg-gray-50 rounded-xl border border-dashed border-gray-200 p-8 text-center">
        <Inbox className="w-8 h-8 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-500">
          Nessuna richiesta ricevuta dal form di contatto.
        </p>
      </div>
    );
  }

  return (
    <ul className="space-y-3">
      {leads.map((l) => {
        const isOpen = openId === l.id;
        const created = l.created_at ? new Date(l.created_at) : null;
        return (
          <li
            key={l.id}
            className="bg-white border border-gray-100 rounded-xl overflow-hidden"
          >
            <button
              type="button"
              onClick={() => setOpenId(isOpen ? null : l.id)}
              className="w-full flex items-start gap-4 p-4 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-[#0FB36B]/10 text-[#0FB36B] flex items-center justify-center shrink-0">
                <UserIcon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <p className="font-montserrat font-semibold text-[#0A1F44] text-sm truncate">
                    {l.name}
                  </p>
                  {created && (
                    <span className="text-[11px] text-gray-400 shrink-0">
                      {created.toLocaleDateString("it-IT")}{" "}
                      {created.toLocaleTimeString("it-IT", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    {l.email}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    {l.phone}
                  </span>
                  {l.plan && (
                    <span className="inline-flex items-center gap-1 text-[#0A1F44]">
                      <StarIcon className="w-3 h-3 text-[#F4C542]" />
                      {l.plan}
                    </span>
                  )}
                </div>
                {!isOpen && l.message && (
                  <p className="text-sm text-gray-600 mt-1.5 line-clamp-1">
                    {l.message}
                  </p>
                )}
              </div>
            </button>
            {isOpen && (
              <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-4 sm:px-6 sm:py-5 space-y-4">
                {l.message ? (
                  <div>
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
                      Messaggio
                    </p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {l.message}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">
                    Nessun messaggio.
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <a
                    href={`mailto:${l.email}?subject=${encodeURIComponent(
                      "Re: richiesta preventivo Energeide"
                    )}`}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#0A1F44] hover:bg-[#0d2855] text-white text-xs font-montserrat font-semibold transition-colors"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    Rispondi via email
                  </a>
                  <a
                    href={`tel:${l.phone.replace(/\s+/g, "")}`}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-white border border-gray-200 hover:border-[#0FB36B] text-[#0A1F44] text-xs font-montserrat font-semibold transition-colors"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    Chiama
                  </a>
                  <button
                    type="button"
                    onClick={() => remove(l.id)}
                    disabled={deletingId === l.id}
                    className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-red-600 hover:bg-red-50 disabled:opacity-50 text-xs font-montserrat font-semibold transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Elimina
                  </button>
                </div>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

// -------------------------------------------------------------------
// SIDEBAR (colonna laterale su desktop, barra orizzontale su mobile)
// -------------------------------------------------------------------
const ADMIN_TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "blog", label: "Blog", icon: Newspaper },
  { id: "projects", label: "Progetti", icon: Building2 },
  { id: "testimonials", label: "Testimonianze", icon: Quote },
  { id: "leads", label: "Richieste", icon: Inbox },
  { id: "media", label: "Media", icon: ImagesIcon },
  { id: "quotes", label: "Preventivatore", icon: Calculator },
];

const Sidebar = ({ active, onChange, onLogout }) => (
  <aside className="lg:w-60 lg:shrink-0 bg-[#0A1F44] print:hidden">
    <div className="lg:sticky lg:top-[88px] lg:h-[calc(100vh-88px)] flex flex-col px-4 py-4 lg:px-5 lg:py-8">
      <div className="flex items-center justify-between lg:block mb-3 lg:mb-8 px-1">
        <div>
          <p className="text-[11px] text-[#0FB36B] font-montserrat font-semibold tracking-[0.2em]">
            AREA RISERVATA
          </p>
        </div>
        <div className="lg:hidden flex items-center gap-4">
          <Link
            to="/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
          >
            <Globe className="w-4 h-4" />
            Vai al sito
          </Link>
          <button
            type="button"
            onClick={onLogout}
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Esci
          </button>
        </div>
      </div>

      <nav
        data-testid="admin-tabs"
        className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible -mx-1 px-1"
      >
        {ADMIN_TABS.map((t) => {
          const Icon = t.icon;
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              type="button"
              data-testid={`tab-${t.id}`}
              onClick={() => onChange(t.id)}
              className={`shrink-0 inline-flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-montserrat font-semibold text-left whitespace-nowrap transition-colors ${
                isActive
                  ? "bg-white text-[#0A1F44]"
                  : "text-white/70 hover:text-white hover:bg-white/10"
              }`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {t.label}
            </button>
          );
        })}
        <Link
          to="/preventivatore"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="tab-area-commerciale"
          className="shrink-0 inline-flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-montserrat font-semibold whitespace-nowrap text-[#0A1F44] bg-[#F4C542] hover:bg-[#e6b838] transition-colors lg:mt-3"
        >
          <Briefcase className="w-4 h-4 shrink-0" />
          Area Commerciale
          <ExternalLink className="w-3.5 h-3.5 ml-auto opacity-60" />
        </Link>
      </nav>

      <Link
        to="/"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="admin-go-site"
        className="hidden lg:inline-flex mt-auto items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-montserrat font-semibold text-white/70 hover:text-white hover:bg-white/10 transition-colors"
      >
        <Globe className="w-4 h-4" />
        Vai al sito
      </Link>
      <button
        data-testid="admin-logout"
        type="button"
        onClick={onLogout}
        className="hidden lg:inline-flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-montserrat font-semibold text-white/60 hover:text-white hover:bg-white/10 transition-colors"
      >
        <LogOut className="w-4 h-4" />
        Esci
      </button>
    </div>
  </aside>
);

// -------------------------------------------------------------------
// MAIN
// -------------------------------------------------------------------
const Admin = () => {
  const [token, setToken] = useAdminToken();
  // Sezione aperta ed elemento in modifica stanno nell'indirizzo:
  // /admin?p=blog  e  /admin?p=blog.detail&id=b4ccaa86
  const [params, setParams] = useSearchParams();
  const [activeTab, subPage] = pageParam(params, "dashboard");
  const setActiveTab = (tab) => setParams({ p: tab });
  const setEditing = (item) =>
    setParams(item ? { p: `${activeTab}.detail`, id: shortId(item.id) } : { p: activeTab });
  const findEditing = (tab, items) =>
    activeTab === tab && subPage === "detail" ? findByShortId(items, params.get("id")) : null;
  const [posts, setPosts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingTestimonials, setLoadingTestimonials] = useState(false);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const editingPost = findEditing("blog", posts);
  const editingProject = findEditing("projects", projects);
  const editingTestimonial = findEditing("testimonials", testimonials);
  const setEditingPost = setEditing;
  const setEditingProject = setEditing;
  const setEditingTestimonial = setEditing;

  const reloadPosts = () => {
    setLoadingPosts(true);
    fetch(`${API_URL}/api/posts`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setPosts(Array.isArray(data) ? data : []))
      .finally(() => setLoadingPosts(false));
  };

  const reloadProjects = () => {
    setLoadingProjects(true);
    fetch(`${API_URL}/api/admin/projects`, {
      headers: { "X-Admin-Token": token },
    })
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((json) => setProjects(Array.isArray(json?.data) ? json.data : []))
      .finally(() => setLoadingProjects(false));
  };

  const reloadTestimonials = () => {
    setLoadingTestimonials(true);
    fetch(`${API_URL}/api/testimonials`)
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((json) => setTestimonials(Array.isArray(json?.data) ? json.data : []))
      .finally(() => setLoadingTestimonials(false));
  };

  const reloadLeads = () => {
    setLoadingLeads(true);
    fetch(`${API_URL}/api/admin/leads`, {
      headers: { "X-Admin-Token": token },
    })
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((json) => setLeads(Array.isArray(json?.data) ? json.data : []))
      .finally(() => setLoadingLeads(false));
  };

  useEffect(() => {
    if (!token) return;
    reloadPosts();
    reloadProjects();
    reloadTestimonials();
    reloadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) {
    return (
      <>
        <Seo title="Area Riservata" path="/admin" />
        <LoginForm onLogin={setToken} />
      </>
    );
  }

  return (
    <>
      <Seo title="Area Riservata" path="/admin" />
      <div className="min-h-[70vh] bg-gray-50 flex flex-col lg:flex-row">
        <Sidebar
          active={activeTab}
          onChange={setActiveTab}
          onLogout={() => setToken(null)}
        />
        <div className="flex-1 min-w-0 px-4 sm:px-6 lg:px-10 py-8 lg:py-10">
          <div className="max-w-6xl">
          {activeTab === "dashboard" ? (
            <AdminDashboard
              token={token}
              posts={posts}
              projects={projects}
              testimonials={testimonials}
              leads={leads}
              onNavigate={setActiveTab}
            />
          ) : activeTab === "media" ? (
            <MediaLibrary token={token} mode="browse" />
          ) : activeTab === "quotes" ? (
            <QuoteAdmin token={token} />
          ) : activeTab === "leads" ? (
            <div>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="text-sm font-montserrat font-bold text-[#0A1F44] uppercase tracking-wider">
                  Richieste dal form contatti
                </h2>
                <span className="text-xs text-gray-400">
                  {leads.length} totali
                </span>
              </div>
              <LeadsList
                leads={leads}
                token={token}
                loading={loadingLeads}
                onDelete={(id) =>
                  setLeads((s) => s.filter((l) => l.id !== id))
                }
              />
            </div>
          ) : activeTab === "testimonials" ? (
            <div className="grid lg:grid-cols-5 gap-8 items-start">
              <div className="lg:col-span-3">
                <TestimonialForm
                  token={token}
                  editing={editingTestimonial}
                  onCancelEdit={() => setEditingTestimonial(null)}
                  onSaved={(isEdit) => {
                    if (isEdit) setEditingTestimonial(null);
                    reloadTestimonials();
                  }}
                />
              </div>
              <div className="lg:col-span-2">
                <h2 className="text-sm font-montserrat font-bold text-[#0A1F44] uppercase tracking-wider mb-3">
                  Testimonianze Pubblicate
                </h2>
                {loadingTestimonials ? (
                  <p className="text-sm text-gray-400">Caricamento…</p>
                ) : (
                  <TestimonialList
                    items={testimonials}
                    token={token}
                    editingId={editingTestimonial?.id}
                    onEdit={(t) => {
                      setEditingTestimonial(t);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    onDelete={(id) => {
                      setTestimonials((s) => s.filter((t) => t.id !== id));
                      if (editingTestimonial?.id === id)
                        setEditingTestimonial(null);
                    }}
                  />
                )}
              </div>
            </div>
          ) : activeTab === "blog" ? (
            <div className="grid lg:grid-cols-5 gap-8 items-start">
              <div className="lg:col-span-3">
                <PostForm
                  token={token}
                  editing={editingPost}
                  onCancelEdit={() => setEditingPost(null)}
                  onSaved={(_data, isEdit) => {
                    if (isEdit) setEditingPost(null);
                    reloadPosts();
                  }}
                />
              </div>
              <div className="lg:col-span-2">
                <h2 className="text-sm font-montserrat font-bold text-[#0A1F44] uppercase tracking-wider mb-3">
                  Post Pubblicati
                </h2>
                {loadingPosts ? (
                  <p className="text-sm text-gray-400">Caricamento…</p>
                ) : (
                  <PostList
                    posts={posts}
                    token={token}
                    editingId={editingPost?.id}
                    onEdit={(p) => {
                      setEditingPost(p);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    onDelete={(id) => {
                      setPosts((s) => s.filter((p) => p.id !== id));
                      if (editingPost?.id === id) setEditingPost(null);
                    }}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="grid lg:grid-cols-5 gap-8 items-start">
              <div className="lg:col-span-3">
                <ProjectForm
                  token={token}
                  editing={editingProject}
                  onCancelEdit={() => setEditingProject(null)}
                  onSaved={(isEdit) => {
                    if (isEdit) setEditingProject(null);
                    reloadProjects();
                  }}
                />
              </div>
              <div className="lg:col-span-2">
                <h2 className="text-sm font-montserrat font-bold text-[#0A1F44] uppercase tracking-wider mb-3">
                  Progetti dal Pannello
                </h2>
                {loadingProjects ? (
                  <p className="text-sm text-gray-400">Caricamento…</p>
                ) : (
                  <ProjectList
                    projects={projects}
                    token={token}
                    editingId={editingProject?.id}
                    onEdit={(p) => {
                      setEditingProject(p);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    onDelete={(id) => {
                      setProjects((s) => s.filter((p) => p.id !== id));
                      if (editingProject?.id === id) setEditingProject(null);
                    }}
                  />
                )}
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Admin;
